/**
 * Inbox state — the user's personal inbox from `/api/amoreg/inbox`.
 *
 * Modelled on state/connections.ts: one warm, cached atom the button reads
 * synchronously; revalidation happens in the background via refresh().
 *
 * The item model is a discriminated union on `kind`. This client is
 * deliberately FORWARD-COMPATIBLE: items are decoded one by one and unknown
 * kinds are dropped, so a server that has grown a new source (regulatory
 * policy-review items are next) never breaks an older client's inbox — the
 * new items simply appear once the client learns to render them.
 *
 * @module state/inbox
 */
import { InboxAgentNotification, InboxInvitationItem, InboxWorkflowApproval } from "@amoreg/contracts";
export type { InboxAgentNotification } from "@amoreg/contracts";
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

export type { InboxInvitationItem } from "@amoreg/contracts";

/**
 * A regulatory change that puts named policies on review. The server does not
 * emit this kind yet — the renderer and this contract land first (exercised
 * by dev sample data), so when monitoring starts producing them the client
 * is already fluent.
 */
const PolicyReviewItemSchema = Schema.Struct({
  kind: Schema.Literal("policy-review"),
  id: Schema.String,
  createdAt: Schema.String,
  regulationTitle: Schema.String,
  jurisdiction: Schema.String,
  severity: Schema.Literals(["high", "medium", "low"]),
  summary: Schema.String,
  affectedPolicies: Schema.Array(Schema.String),
});
export type InboxPolicyReviewItem = typeof PolicyReviewItemSchema.Type;

/** Every kind this client can render. New kinds extend this union. */
const InboxItemSchema = Schema.Union([
  InboxInvitationItem,
  PolicyReviewItemSchema,
  InboxAgentNotification,
  InboxWorkflowApproval,
]);

/**
 * A workflow run waiting on the reader's approval, as the inbox shows it:
 * `createdAt` is its latest working-morning reminder (or when it began
 * waiting, before the first), and the reminder is part of its `id`, so each
 * morning it is new, and unread, again.
 */
export interface InboxApprovalItem extends InboxWorkflowApproval {
  /** When the run began waiting at the step. */
  readonly waitingSince: string;
}
export type InboxItem = Exclude<typeof InboxItemSchema.Type, { readonly kind: "workflow-approval" }> | InboxApprovalItem;

/** The working morning a reminder lands at: 09:00, Monday to Friday, in the reader's own zone. */
const REMINDER_HOUR = 9;

/**
 * When a waiting approval last came due for a reminder: the latest weekday
 * 09:00 (the reader's local time) after it began waiting and not after `now`,
 * or null while none has passed.
 */
export function approvalReminderAt(waitingSince: string, now: Date): Date | null {
  const since = Date.parse(waitingSince);
  if (!Number.isFinite(since)) return null;
  const slot = new Date(now);
  slot.setHours(REMINDER_HOUR, 0, 0, 0);
  if (slot.getTime() > now.getTime()) slot.setDate(slot.getDate() - 1);
  // Back to the latest weekday at or before it (at most two days).
  while (slot.getDay() === 0 || slot.getDay() === 6) slot.setDate(slot.getDate() - 1);
  return slot.getTime() > since ? slot : null;
}

/** An approval as the inbox shows it at `now` (see {@link InboxApprovalItem}). */
export function withApprovalReminder(item: InboxWorkflowApproval, now: Date): InboxApprovalItem {
  const reminder = approvalReminderAt(item.createdAt, now);
  return reminder === null
    ? { ...item, waitingSince: item.createdAt }
    : { ...item, waitingSince: item.createdAt, createdAt: reminder.toISOString(), id: `${item.id}@${reminder.toISOString()}` };
}

const CapabilitiesSchema = Schema.Struct({
  acceptInvitation: Schema.Boolean,
});
export type InboxCapabilities = typeof CapabilitiesSchema.Type;

/** The wire envelope: items stay unknown so each decodes independently. */
const WireSnapshotSchema = Schema.Struct({
  items: Schema.Array(Schema.Unknown),
  capabilities: CapabilitiesSchema,
});

interface InboxSnapshot {
  readonly items: ReadonlyArray<InboxItem>;
  readonly capabilities: InboxCapabilities;
  readonly available: boolean;
}

/**
 * "This server has no inbox" — a 404, or the SPA fallback answering where no
 * route exists. Only THIS state hides the button; a transient failure keeps
 * it visible with a retry inside, because a flaky first fetch must not erase
 * chrome for the whole session.
 */
const UNAVAILABLE: InboxSnapshot = {
  items: [],
  capabilities: { acceptInvitation: false },
  available: false,
};

const decodeWireSnapshot = Schema.decodeUnknownEffect(WireSnapshotSchema);
const decodeItem = Schema.decodeUnknownEffect(InboxItemSchema);

class InboxRequestError extends Schema.TaggedErrorClass<InboxRequestError>()("InboxRequestError", {
  status: Schema.Number,
}) {}

const fetchSnapshot: Effect.Effect<InboxSnapshot, unknown> = Effect.gen(function* () {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/inbox");
  const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
  // A 404 is "this server has no inbox", a normal state. So is an HTML answer:
  // dev servers fall through to the SPA on unrouted paths, which is the same
  // fact wearing a 200.
  if (response.status === 404) {
    return UNAVAILABLE;
  }
  if (!(response.headers.get("content-type") ?? "").includes("application/json")) {
    return UNAVAILABLE;
  }
  if (!response.ok) {
    return yield* Effect.fail(new InboxRequestError({ status: response.status }));
  }
  const json = yield* Effect.tryPromise(() => response.json());
  const wire = yield* decodeWireSnapshot(json);
  // Per-item decode: an unknown kind is a server ahead of us, not a failure.
  const items = yield* Effect.forEach(wire.items, (item) => decodeItem(item).pipe(Effect.option));
  const now = new Date();
  return {
    items: items
      .filter(Option.isSome)
      .map((item): InboxItem => (item.value.kind === "workflow-approval" ? withApprovalReminder(item.value, now) : item.value)),
    capabilities: wire.capabilities,
    available: true,
  } satisfies InboxSnapshot;
});

const inboxRuntime = Atom.runtime(Layer.empty);
const inboxAtom = inboxRuntime.atom(fetchSnapshot).pipe(Atom.withLabel("web-amoreg:inbox"));

/** Re-read the inbox now (after deciding an approval elsewhere, say). */
export function refreshInbox(): void {
  appAtomRegistry.refresh(inboxAtom);
}

const AcceptanceSchema = Schema.Struct({
  organizationId: Schema.String,
  organizationSlug: Schema.String,
  organizationName: Schema.String,
  role: Schema.Literals(["admin", "member"]),
});
export type InvitationAcceptance = typeof AcceptanceSchema.Type;
const decodeAcceptance = Schema.decodeUnknownSync(AcceptanceSchema);

export type InvitationAnswer =
  | { readonly ok: true; readonly action: "accept"; readonly joined: InvitationAcceptance }
  | { readonly ok: true; readonly action: "decline" }
  | { readonly ok: false; readonly message: string };

/**
 * Accept or decline an invitation addressed to the signed-in person. The
 * server forwards to the domain API, which checks the address; an accept
 * answers with the organization that opened. Exported for tests and the hook.
 */
export async function answerInvitation(
  invitationId: string,
  action: "accept" | "decline",
): Promise<InvitationAnswer> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/inbox/invitations/answer", {
    invitationId,
    action,
  });
  const fallback =
    action === "accept"
      ? "The invitation could not be accepted."
      : "The invitation could not be declined.";
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      try {
        const parsed = JSON.parse(text) as { message?: string };
        return { ok: false, message: parsed.message ?? fallback };
      } catch {
        return { ok: false, message: text.length > 0 ? text : fallback };
      }
    }
    if (action === "decline") return { ok: true, action };
    return { ok: true, action, joined: decodeAcceptance(await response.json()) };
  } catch {
    return { ok: false, message: fallback };
  }
}

export interface InboxView {
  readonly items: ReadonlyArray<InboxItem>;
  readonly capabilities: InboxCapabilities;
  /** False when the server has no inbox feature. */
  readonly available: boolean;
  /** True until the first response arrives. */
  readonly isPending: boolean;
  /** True when the last fetch failed (server error / network), retryable. */
  readonly failed: boolean;
  readonly refresh: () => void;
  /** Answer an invitation; the inbox refreshes so the item retires. */
  readonly answer: (
    invitationId: string,
    action: "accept" | "decline",
  ) => Promise<InvitationAnswer>;
}

let inboxConsumers = 0;
let stopInboxRefresh: (() => void) | undefined;
function subscribeInboxRefresh() {
  if (++inboxConsumers === 1) {
    const refreshVisible = () => {
      if (document.visibilityState === "visible") appAtomRegistry.refresh(inboxAtom);
    };
    const interval = window.setInterval(refreshVisible, 30_000);
    window.addEventListener("focus", refreshVisible);
    document.addEventListener("visibilitychange", refreshVisible);
    stopInboxRefresh = () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshVisible);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }
  return () => {
    if (--inboxConsumers === 0) stopInboxRefresh?.();
  };
}

export function useInbox(): InboxView {
  useEffect(subscribeInboxRefresh, []);
  const result = useAtomValue(inboxAtom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(inboxAtom);
  }, []);

  const answer = useCallback(async (invitationId: string, action: "accept" | "decline") => {
    const outcome = await answerInvitation(invitationId, action);
    if (outcome.ok) appAtomRegistry.refresh(inboxAtom);
    return outcome;
  }, []);

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    const failed = result._tag === "Failure";
    return {
      items: snapshot?.items ?? [],
      capabilities: snapshot?.capabilities ?? UNAVAILABLE.capabilities,
      // A failure is not "the feature does not exist": keep the button, put
      // the problem inside it.
      available: (snapshot?.available ?? false) || failed,
      isPending: result.waiting && snapshot === null,
      failed,
      refresh,
      answer,
    };
  }, [answer, result, refresh]);
}
